import React, { useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { UserRound, Users, FileText, MessageSquare, Calendar, Image as ImageIcon, Upload, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../PageHeader';
import { teamSites } from '../data';
import { useApp, TeamPost } from '../store';
import { downscaleImage } from '../image';
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
/* Composer for a new team post — supports text + an optional image. */
function TeamComposer({
  teamId
}: {
  teamId: string;
}) {
  const {
    addTeamPost,
    user,
    hasIdentity
  } = useApp();
  const fileRef = useRef<HTMLInputElement>(null);
  const [body, setBody] = useState('');
  const [image, setImage] = useState('');
  const [busy, setBusy] = useState(false);
  const onImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file.');
      return;
    }
    setBusy(true);
    try {
      setImage(await downscaleImage(file, 1024, 0.8));
    } catch {
      toast.error('Sorry, we couldn’t process that image.');
    } finally {
      setBusy(false);
    }
  };
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim() && !image) return;
    addTeamPost({
      teamId,
      body: body.trim(),
      image: image || undefined
    });
    setBody('');
    setImage('');
  };
  return <form onSubmit={submit} className="bg-white rounded-xl border border-navy-100 p-4" data-id="element-748">
      <div className="flex gap-3" data-id="element-749">
        {hasIdentity ? <img src={user.avatar} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" data-id="element-750" /> : <div className="w-10 h-10 rounded-full bg-navy-50 text-navy-300 flex items-center justify-center shrink-0" data-id="element-751">
            <UserRound className="w-5 h-5" data-id="element-752" />
          </div>}
        <div className="flex-1 min-w-0" data-id="element-753">
          <textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Share an update with the team…" rows={2} className="w-full resize-none px-3 py-2 rounded-lg bg-navy-50 border border-transparent focus:border-gold-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-gold-400/30 text-sm text-navy-900 placeholder:text-navy-400" data-id="element-754" />
          {image && <div className="relative mt-2 inline-block" data-id="element-755">
              <img src={image} alt="Attached" className="max-h-40 rounded-lg border border-navy-100" data-id="element-756" />
              <button type="button" onClick={() => setImage('')} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-navy-600 text-white flex items-center justify-center hover:bg-navy-700" aria-label="Remove image" data-id="element-757">
                <X className="w-3.5 h-3.5" data-id="element-758" />
              </button>
            </div>}
          <div className="flex items-center justify-between mt-2" data-id="element-759">
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="inline-flex items-center gap-1.5 text-sm font-medium text-navy-500 hover:text-navy-800 px-2 py-1 rounded-lg hover:bg-navy-50 disabled:opacity-50" data-id="element-760">
              <ImageIcon className="w-4 h-4" data-id="element-761" />
              {busy ? 'Processing…' : 'Photo'}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={onImage} className="hidden" data-id="element-762" />
            <button type="submit" disabled={!body.trim() && !image || busy} className="inline-flex items-center gap-1.5 text-sm font-bold text-navy-900 bg-gold-500 px-4 py-2 rounded-lg hover:bg-gold-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-763">
              <Send className="w-4 h-4" data-id="element-764" /> Post
            </button>
          </div>
        </div>
      </div>
    </form>;
}
/* A single team post with inline comments. */
function TeamPostCard({
  post,
  teamId
}: {
  post: TeamPost;
  teamId: string;
}) {
  const {
    addTeamComment,
    user,
    hasIdentity
  } = useApp();
  const [draft, setDraft] = useState('');
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.trim()) return;
    addTeamComment(teamId, post.id, draft.trim());
    setDraft('');
  };
  return <motion.article initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} className="bg-white rounded-xl border border-navy-100 overflow-hidden" data-id="element-765">
      <div className="p-4" data-id="element-766">
        <div className="flex items-center gap-3 mb-3" data-id="element-767">
          <img src={post.avatar} alt={post.author} className="w-10 h-10 rounded-full object-cover" data-id="element-768" />
          <div data-id="element-769">
            <p className="text-sm font-bold text-navy-900" data-id="element-770">{post.author}</p>
            <p className="text-xs text-navy-400" data-id="element-771">
              {post.role} · {post.time}
            </p>
          </div>
        </div>
        {post.body && <p className="text-sm text-navy-800 leading-relaxed whitespace-pre-wrap" data-id="element-772">
            {post.body}
          </p>}
      </div>
      {post.image && <img src={post.image} alt="" className="w-full max-h-96 object-cover" data-id="element-773" />}
      <div className="px-4 py-3 border-t border-navy-50 space-y-3" data-id="element-774">
        {post.comments.map(c => <div key={c.id} className="flex gap-2.5" data-id="element-775">
            <img src={c.avatar} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" data-id="element-776" />
            <div className="bg-navy-50 rounded-lg px-3 py-2 flex-1" data-id="element-777">
              <p className="text-xs font-semibold text-navy-900" data-id="element-778">
                {c.author}{' '}
                <span className="font-normal text-navy-400" data-id="element-779">· {c.time}</span>
              </p>
              <p className="text-sm text-navy-700" data-id="element-780">{c.body}</p>
            </div>
          </div>)}
        <form onSubmit={submit} className="flex gap-2" data-id="element-781">
          <input value={draft} onChange={e => setDraft(e.target.value)} placeholder={hasIdentity ? 'Add a comment…' : 'Introduce yourself to comment…'} className="flex-1 px-3 py-2 rounded-lg bg-navy-50 border border-transparent focus:border-gold-400 focus:bg-white focus:outline-none text-sm text-navy-900" data-id="element-782" />
          <button type="submit" disabled={!draft.trim()} className="px-3 py-2 rounded-lg text-navy-500 hover:bg-navy-50 disabled:opacity-40" aria-label="Send comment" data-id="element-783">
            <Send className="w-4 h-4" data-id="element-784" />
          </button>
        </form>
      </div>
    </motion.article>;
}
export function TeamDetailPage() {
  const {
    id = ''
  } = useParams();
  const team = teamSites.find(t => t.id === id);
  const {
    getTeamPosts,
    getTeamFiles,
    addTeamFile
  } = useApp();
  const uploadRef = useRef<HTMLInputElement>(null);
  if (!team) {
    return <div className="text-center py-20" data-id="element-785">
        <p className="text-navy-500 mb-4" data-id="element-786">That team couldn't be found.</p>
        <Link to="/teams" className="text-gold-600 font-semibold" data-id="element-787">
          Back to Teams & Sites
        </Link>
      </div>;
  }
  const posts = getTeamPosts(team.id);
  const files = getTeamFiles(team.id);
  const onUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    addTeamFile({
      teamId: team.id,
      name: file.name,
      size: formatBytes(file.size)
    });
    toast.success(`${file.name} shared with ${team.name}.`);
  };
  return <div className="max-w-5xl mx-auto" data-id="element-788">
      <PageHeader title={team.name} backTo="/teams" backLabel="Back to Teams" data-id="element-789" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6" data-id="element-790">
        {/* Main — about + feed */}
        <div className="lg:col-span-2 space-y-6" data-id="element-791">
          <div className="bg-white rounded-xl border border-navy-100 overflow-hidden" data-id="element-792">
            <div className={`h-2 w-full ${team.color}`} data-id="element-793" />
            <div className="p-6" data-id="element-794">
              <h2 className="font-serif font-bold text-xl text-navy-900 mb-2" data-id="element-795">
                About {team.name}
              </h2>
              <p className="text-navy-600 leading-relaxed" data-id="element-796">
                {team.description}
              </p>
            </div>
          </div>

          <div data-id="element-797">
            <h2 className="font-serif font-bold text-xl text-navy-900 flex items-center gap-2 mb-3" data-id="element-798">
              <MessageSquare className="w-5 h-5 text-gold-500" data-id="element-799" /> Team Feed
            </h2>
            <div className="space-y-4" data-id="element-800">
              <TeamComposer teamId={team.id} data-id="element-801" />
              <AnimatePresence data-id="element-802">
                {posts.length > 0 ? posts.map(p => <TeamPostCard key={p.id} post={p} teamId={team.id} data-id="element-803" />) : <div className="text-center py-8 bg-navy-50 rounded-xl border border-dashed border-navy-200" data-id="element-804">
                    <p className="text-navy-500 text-sm" data-id="element-805">
                      No posts yet — be the first to share an update with{' '}
                      {team.name}.
                    </p>
                  </div>}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Sidebar — members, files, events */}
        <div className="space-y-6" data-id="element-806">
          <div className="bg-white rounded-xl border border-navy-100 p-6" data-id="element-807">
            <h3 className="font-bold text-navy-900 mb-4 flex items-center gap-2" data-id="element-808">
              <Users className="w-5 h-5 text-navy-400" data-id="element-809" /> Members (
              {team.members})
            </h3>
            <div className="flex flex-wrap gap-2" data-id="element-810">
              {Array.from({
              length: Math.min(team.members, 7)
            }).map((_, i) => <div key={i} className="w-10 h-10 rounded-full bg-navy-50 text-navy-300 flex items-center justify-center border-2 border-white shadow-sm" aria-hidden="true" data-id="element-811">
                  <UserRound className="w-5 h-5" data-id="element-812" />
                </div>)}
              {team.members > 7 && <div className="w-10 h-10 rounded-full bg-navy-50 flex items-center justify-center text-xs font-bold text-navy-500 border-2 border-white shadow-sm" data-id="element-813">
                  +{team.members - 7}
                </div>}
            </div>
            <p className="text-xs text-navy-400 mt-3" data-id="element-814">
              The full member directory loads from this team’s workspace once
              connected.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-navy-100 p-6" data-id="element-815">
            <div className="flex items-center justify-between mb-4" data-id="element-816">
              <h3 className="font-bold text-navy-900 flex items-center gap-2" data-id="element-817">
                <FileText className="w-5 h-5 text-navy-400" data-id="element-818" /> Files
              </h3>
              <button onClick={() => uploadRef.current?.click()} className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy-700 bg-navy-50 px-2.5 py-1.5 rounded-lg hover:bg-navy-100 transition-colors" data-id="element-819">
                <Upload className="w-3.5 h-3.5" data-id="element-820" /> Upload
              </button>
              <input ref={uploadRef} type="file" onChange={onUpload} className="hidden" data-id="element-821" />
            </div>
            {files.length > 0 ? <ul className="space-y-3" data-id="element-822">
                {files.map(f => <li key={f.id} className="flex items-start gap-3" data-id="element-823">
                    <FileText className="w-4 h-4 text-gold-500 mt-0.5 shrink-0" data-id="element-824" />
                    <div className="min-w-0" data-id="element-825">
                      <p className="text-sm font-medium text-navy-800 truncate" data-id="element-826">
                        {f.name}
                      </p>
                      <p className="text-xs text-navy-400" data-id="element-827">
                        {f.size} · {f.uploaderName} · {f.time}
                      </p>
                    </div>
                  </li>)}
              </ul> : <div className="text-center py-6 bg-navy-50 rounded-lg border border-dashed border-navy-200" data-id="element-828">
                <p className="text-navy-500 text-sm" data-id="element-829">
                  No files yet — upload one to share it with the team.
                </p>
              </div>}
          </div>

          <div className="bg-white rounded-xl border border-navy-100 p-6" data-id="element-830">
            <h3 className="font-bold text-navy-900 mb-4 flex items-center gap-2" data-id="element-831">
              <Calendar className="w-5 h-5 text-navy-400" data-id="element-832" /> Upcoming Events
            </h3>
            <div className="text-center py-6 bg-navy-50 rounded-lg" data-id="element-833">
              <p className="text-navy-500 text-sm" data-id="element-834">No upcoming events.</p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}